import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ISites } from '@df/core/df-core.interfaces';
import { ISizeName } from '@df/core/service/client';
import { MultisiteService } from '@df/core/service/multisite';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ProcessorService } from '@df/ng/processor.service';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { MultisiteRedirectService } from '@df/ui/multisite/multisite-redirect.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { AbstractCmsBlockComponent } from '../../cms/block/abstract-cms-block.component';
import { RadioComponent } from '../../ui/form/radio';
import { MultisiteModalInitiatorService } from './multisite-modal-initiator.service';
import { NMultisiteModal } from './multisite-modal.interfaces';

const storeOptionsFn = () =>
    ProcessorService.injector.get(MultisiteService).all.map(i => ({
        key: i.siteId,
        title: i.name
    }));

/**
 * TODO: (iCMS) check component
 */
@Component({
    selector: 'multisite_modal',
    templateUrl: './multisite-modal.html',
    styleUrl: './multisite-modal.scss',
    standalone: true,
    imports: [CommonModule, FormsModule, ICmsClientModule, DfModuleImageModule, RadioComponent, RadioGroupDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@DfCmsModal({
    id: NMultisiteModal.componentId,
    position: 'custom',
    name: NMultisiteModal.componentId,
    config: (sizeId: ISizeName) => ({
        closeOnNavigation: true,
        height: sizeId === 'S' ? 'auto' : '100%',
        minWidth: undefined,
        maxWidth: undefined,
        width: sizeId === 'S' ? '100%' : '31.5em',
        position: {
            top: sizeId === 'S' ? '' : '0',
            bottom: sizeId === 'S' ? '0' : '',
            left: '0'
        },
        modalClass: 'bg-col-w p-t-7 p-r-10 p-l-10 p-a-4-s m-l-4-s m-r height-auto-s',
        transformOptions: sizeId === 'S' ? 'translate3d(0%, 100%, 0px)' : 'translate3d(-100%, 0px, 0px)',
        transitionOptions: '500ms ease-in-out'
    }),
    blockGroup: 'modals',
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeName => ['S', 'M', 'L', 'X'],
    priority: 30,
    initiators: [MultisiteModalInitiatorService],
})
@ICmsComponent<MultisiteModalComponent>({
    id: NMultisiteModal.componentId,
    onDemandOnly: true,
    block: {
        url: NMultisiteModal.componentId
    },
    name: 'Geolocation popup',
    description: 'Appears when user visits the store from a country that matches another one. Offers user to change store.',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'testing',
            title: 'CMS testing setup'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cmsCountryCode',
            title: 'Force country code',
            description: 'Put any country code to preview popup in that variant',
            required: false
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'variants',
            title: 'Variant',
            description: 'First variant that matches user country code is used for display.',
            itemName: 'Variant',
            required: true,
            defaultSize: 3,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'countries',
                    title: 'Countries',
                    description: 'Coma separated list of 2-char country-codes, see https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    type: NICmsContentField.EFieldType.choice,
                    key: 'defaultSiteId',
                    title: 'Default site',
                    options: storeOptionsFn,
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'label-general',
                    title: 'General'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'heading',
                    title: 'Top heading',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'copy',
                    title: 'Top copy',
                    required: false
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'buttonCopy',
                    title: 'Confirmation button copy',
                    required: true
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'stores',
                    title: 'Store links',
                    itemName: 'Store link',
                    required: true,
                    defaultSize: 3,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            type: NICmsContentField.EFieldType.choice,
                            key: 'siteId',
                            title: 'Website',
                            description: 'Please select website to link to. If website is off the link will be skipped.',
                            options: storeOptionsFn,
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.image,
                            key: 'icon',
                            title: 'Icon',
                            required: false
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'title',
                            title: 'Title',
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'subtitle',
                            title: 'Subtitle',
                            required: false
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'details',
                            title: 'Details',
                            required: false
                        }
                    ]
                }
            ]
        }
    ]
})
export class MultisiteModalComponent extends AbstractCmsBlockComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: NMultisiteModal.IBlockContent;

    /**
     * selected store we will redirect to
     */
    selectedStore?: keyof ISites;

    constructor(
        private multisiteRedirectService: MultisiteRedirectService,
        private multisiteService: MultisiteService,
        @Optional() private modalRef: ModalRef<MultisiteModalComponent>,
        @Optional() @Inject(MODAL_DATA) protected data: NMultisiteModal.IInitiatorData
    ) {
        super();
    }

    override ngOnInit() {
        super.ngOnInit();

        this.modalRef?.beforeClosed().subscribe(() => this.confirm());

        const expectedSelection = this.contentVariant?.defaultSiteId || '';
        const stores = this.stores;

        this.pick(stores.find(i => i.siteId === expectedSelection) || stores[0]);
    }

    /**
     * Retrieves a variant matching currently previewed content
     */
    get contentVariant(): NMultisiteModal.IBlockContentVariant | undefined {
        const variants = this.content?.variants;

        if (!variants) {
            return undefined;
        }

        const code = ((this.client.isCms && this.content.cmsCountryCode) || this.data?.matchingCountry || '').toUpperCase();

        return (
            (code &&
                variants.find(variant =>
                    variant.countries
                        ?.toUpperCase()
                        .split(',')
                        .find(countryCode => countryCode.trim() === code)
                )) ||
            variants[0]
        );
    }

    /**
     * List of stores
     * only enabled ones are allowed
     */
    get stores() {
        return this.contentVariant?.stores.filter(i => !!this.multisiteService.toMultisiteItem(i.siteId)) || [];
    }

    pick(store: NMultisiteModal.IBlockContentStore | string) {
        this.selectedStore = store ? (typeof store === 'string' ? store : store.siteId) : undefined;
        this.detectChanges();
    }

    /**
     * Confirm location change
     */
    confirm(): void {
        // do not show again
        // this.disableGlobal();
        // jump to selected website
        this.goTo(<string>this.selectedStore);
    }

    /**
     * Changes location using multisite service
     * what keeps current path (or uses alternate) and saves website preference
     */
    protected goTo(siteId: number | string, $event?: Event) {
        if (this.multisiteRedirectService.to(siteId)) {
            $event?.preventDefault();
        } else {
            this.close();
        }
    }

    /**
     * Closes the modal
     */
    protected close() {
        this.modalRef?.close();
    }
}
